import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { requiresAuth: false, layout: 'blank' },
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/Register.vue'),
    meta: { requiresAuth: false, layout: 'blank' },
  },
  {
    path: '/',
    component: () => import('@/layouts/DefaultLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      // ===== 首页 =====
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/views/Dashboard.vue'),
        meta: { title: '首页', icon: 'HomeFilled', permissionId: 'DASHBOARD_VIEW' },
      },

      // ===== 教务 =====
      {
        path: 'schedule',
        name: 'Schedule',
        component: () => import('@/views/ScheduleManagement.vue'),
        meta: { title: '排课表', icon: 'Calendar', permissionId: 'SCHEDULE_VIEW', group: '教务' },
      },
      {
        path: 'bookings',
        name: 'Bookings',
        component: () => import('@/views/BookingManagement.vue'),
        meta: { title: '预约记录', icon: 'Tickets', permissionId: 'BOOKING_VIEW', group: '教务' },
      },
      {
        path: 'courses',
        name: 'Courses',
        component: () => import('@/views/CourseManagement.vue'),
        meta: { title: '课程模板', icon: 'Reading', permissionId: 'COURSE_VIEW', group: '教务' },
      },
      {
        path: 'classes',
        name: 'Classes',
        component: () => import('@/views/ClassManagement.vue'),
        meta: { title: '班级管理', icon: 'Coin', permissionId: 'CLASS_VIEW', group: '教务' },
      },

      // ===== 学员 =====
      {
        path: 'students',
        name: 'Students',
        component: () => import('@/views/StudentManagement.vue'),
        meta: { title: '学员管理', icon: 'User', permissionId: 'STUDENT_VIEW', group: '学员' },
      },
      {
        path: 'entitlements',
        name: 'Entitlements',
        component: () => import('@/views/EntitlementManagement.vue'),
        meta: { title: '会员卡', icon: 'Wallet', permissionId: 'ENTITLEMENT_VIEW', group: '学员' },
      },
      {
        path: 'entitlement-templates',
        name: 'EntitlementTemplates',
        component: () => import('@/views/EntitlementTemplateManagement.vue'),
        meta: { title: '会员卡模板', icon: 'Tickets', permissionId: 'ENTITLEMENT_VIEW', group: '学员' },
      },
      {
        path: 'attendance',
        name: 'Attendance',
        component: () => import('@/views/AttendanceManagement.vue'),
        meta: { title: '课消记录', icon: 'CircleCheck', permissionId: 'ATTENDANCE_VIEW', group: '学员' },
      },
      {
        path: 'family-groups',
        name: 'FamilyGroups',
        component: () => import('@/views/FamilyGroupManagement.vue'),
        meta: { title: '家庭组', icon: 'User', permissionId: 'FAMILY_GROUP_VIEW', group: '学员' },
      },

      // ===== 线索 =====
      {
        path: 'leads',
        name: 'Leads',
        component: () => import('@/views/LeadManagement.vue'),
        meta: { title: '线索', icon: 'Promotion', permissionId: 'LEAD_VIEW', group: '线索' },
      },

      // ===== 合同 =====
      {
        path: 'contracts',
        component: () => import('@/views/ContractManagement.vue'),
        meta: { permissionId: 'CONTRACT_VIEW' },
        redirect: '/contracts/list',
        children: [
          {
            path: 'templates',
            name: 'ContractTemplates',
            component: () => import('@/components/ContractTemplateTab.vue'),
            meta: { title: '合同模板', group: '合同', permissionId: 'CONTRACT_VIEW' },
          },
          {
            path: 'list',
            name: 'ContractList',
            component: () => import('@/components/ContractListTab.vue'),
            meta: { title: '合同列表', group: '合同', permissionId: 'CONTRACT_VIEW' },
          },
        ],
      },

      // ===== 财务 =====
      {
        path: 'finance',
        component: () => import('@/views/FinanceManagement.vue'),
        meta: { permissionId: 'FINANCE_VIEW' },
        redirect: '/finance/membership',
        children: [
          {
            path: 'membership',
            name: 'FinanceMembership',
            component: () => import('@/components/FinanceMembershipTab.vue'),
            meta: { title: '会员卡账单', group: '财务', permissionId: 'FINANCE_VIEW' },
          },
          {
            path: 'mall',
            name: 'FinanceMall',
            component: () => import('@/components/FinanceMallTab.vue'),
            meta: { title: '商城账单', group: '财务', permissionId: 'FINANCE_VIEW' },
          },
          {
            path: 'other',
            name: 'FinanceOther',
            component: () => import('@/components/FinanceOtherTab.vue'),
            meta: { title: '其它账单', group: '财务', permissionId: 'FINANCE_VIEW' },
          },
          // B20/FIN-19：合同账单子路由已删除（决4：财务保留三tab=会员卡/商城/其它）
        ],
      },

      // ===== 审批 =====
      {
        path: 'approvals',
        component: () => import('@/views/ApprovalManagement.vue'),
        meta: { permissionId: 'APPROVAL_VIEW' },
        redirect: '/approvals/pending',
        children: [
          {
            path: 'pending',
            name: 'ApprovalPending',
            component: () => import('@/components/ApprovalPendingTab.vue'),
            meta: { title: '待办审批', group: '审批', permissionId: 'APPROVAL_VIEW' },
          },
          {
            path: 'contracts',
            name: 'ApprovalContracts',
            component: () => import('@/components/ApprovalContractTab.vue'),
            meta: { title: '合同审批', group: '审批', permissionId: 'APPROVAL_VIEW' },
          },
          {
            path: 'membership',
            name: 'ApprovalMembership',
            component: () => import('@/components/ApprovalMembershipTab.vue'),
            meta: { title: '会员卡审批', group: '审批', permissionId: 'APPROVAL_VIEW' },
          },
          {
            path: 'course',
            name: 'ApprovalCourse',
            component: () => import('@/components/ApprovalCourseTab.vue'),
            meta: { title: '课程审批', group: '审批', permissionId: 'APPROVAL_VIEW' },
          },
          {
            path: 'finance',
            name: 'ApprovalFinance',
            component: () => import('@/components/ApprovalFinanceTab.vue'),
            meta: { title: '财务审批', group: '审批', permissionId: 'APPROVAL_VIEW' },
          },
        ],
      },

      // ===== 校区 =====
      {
        path: 'settings/brand',
        name: 'BrandSettings',
        component: () => import('@/views/BrandSettings.vue'),
        meta: { title: '品牌设置', icon: 'Brush', permissionId: 'BRAND_VIEW', group: '品牌' },
      },
      {
        path: 'settings/campuses',
        name: 'CampusManagement',
        component: () => import('@/views/CampusManagement.vue'),
        meta: { title: '校区列表', icon: 'School', permissionId: 'CAMPUS_VIEW', group: '品牌' },
      },

      // ===== 员工 =====
      {
        path: 'settings/staffs',
        name: 'StaffManagement',
        component: () => import('@/views/StaffManagement.vue'),
        meta: { title: '员工管理', icon: 'UserFilled', permissionId: 'STAFF_VIEW', group: '员工' },
      },
      {
        path: 'settings/roles',
        name: 'RoleManagement',
        component: () => import('@/views/RoleManagement.vue'),
        meta: { title: '角色管理', icon: 'Key', permissionId: 'ROLE_VIEW', group: '员工' },
      },
      {
        path: 'settings/permissions',
        name: 'PermissionConfig',
        component: () => import('@/views/PermissionConfig.vue'),
        meta: { title: '权限配置', icon: 'Lock', permissionId: 'PERMISSION_VIEW', group: '员工' },
      },
      {
        path: 'payroll',
        name: 'Payroll',
        component: () => import('@/views/PayrollManagement.vue'),
        meta: { title: '薪资规格', icon: 'Money', permissionId: 'PAYROLL_VIEW', group: '员工' },
      },

      // ===== 日志 =====
      {
        path: 'settings/audit',
        name: 'AuditLogs',
        component: () => import('@/views/AuditLogs.vue'),
        meta: { title: '操作日志', icon: 'Document', permissionId: 'AUDIT_VIEW', group: '日志' },
      },

      // ===== 数据 =====
      {
        path: 'analytics',
        name: 'Analytics',
        component: () => import('@/views/AnalyticsDashboard.vue'),
        meta: { title: '数据分析', icon: 'TrendCharts', permissionId: 'ANALYTICS_VIEW', group: '数据' },
      },

      // ===== 库存 =====
      {
        path: 'inventory',
        name: 'Inventory',
        component: () => import('@/views/InventoryManagement.vue'),
        meta: { title: '库存管理', icon: 'Box', permissionId: 'INVENTORY_VIEW', group: '库存' },
      },

      // ===== 插件 =====
      {
        path: 'plugins',
        name: 'Plugins',
        component: () => import('@/views/PluginCenter.vue'),
        meta: { title: '插件中心', icon: 'Histogram', permissionId: 'PLUGIN_VIEW', group: '插件' },
      },

      // ===== P2 流程编排（方案库） =====
      {
        path: 'flow',
        name: 'FlowManagement',
        component: () => import('@/views/FlowManagement.vue'),
        meta: { title: '方案库管理', icon: 'Share', permissionId: 'FLOW_VIEW', group: '流程编排' },
      },

      // ===== 系统 =====
      {
        path: 'settings',
        name: 'SystemSettings',
        component: () => import('@/views/SystemSettings.vue'),
        meta: { title: '系统设置', icon: 'Setting', permissionId: 'SETTINGS_VIEW', group: '系统' },
      },
    ],
  },
  // ===== H5 移动端 =====
  {
    path: '/h5',
    component: () => import('@/layouts/H5Layout.vue'),
    meta: { requiresAuth: true },
    redirect: '/h5/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'H5Dashboard',
        component: () => import('@/views/h5/H5Dashboard.vue'),
        meta: { title: '首页', h5Tab: 'home' },
      },
      {
        path: 'schedule',
        name: 'H5Schedule',
        component: () => import('@/views/h5/H5Schedule.vue'),
        meta: { title: '排课', h5Tab: 'schedule' },
      },
      {
        path: 'students',
        name: 'H5Students',
        component: () => import('@/views/h5/H5Students.vue'),
        meta: { title: '学员', h5Tab: 'students' },
      },
      {
        path: 'entitlements',
        name: 'H5Entitlements',
        component: () => import('@/views/h5/H5Entitlements.vue'),
        meta: { title: '会员卡', h5Tab: 'entitlements' },
      },
      {
        path: 'profile',
        name: 'H5Profile',
        component: () => import('@/views/h5/H5Profile.vue'),
        meta: { title: '我的', h5Tab: 'profile' },
      },
      {
        path: 'attendance',
        name: 'H5Attendance',
        component: () => import('@/views/h5/H5Attendance.vue'),
        meta: { title: '课消记录' },
      },
      {
        path: 'students/:id',
        name: 'H5StudentDetail',
        component: () => import('@/views/h5/H5StudentDetail.vue'),
        meta: { title: '学员详情' },
      },
      {
        path: 'entitlements/:id',
        name: 'H5EntitlementDetail',
        component: () => import('@/views/h5/H5EntitlementDetail.vue'),
        meta: { title: '会员卡详情' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  if (to.meta.title) {
    document.title = `${to.meta.title} - ${import.meta.env.VITE_APP_TITLE}`
  }

  if (to.meta.requiresAuth !== false) {
    if (!authStore.isAuthenticated) {
      await authStore.restoreSession()
    }
    if (!authStore.isAuthenticated) {
      next({ name: 'Login', query: { redirect: to.fullPath } })
      return
    }
  } else if (to.name === 'Login') {
    // 0924：已登录访问 /login 自动跳首页（刷新后 store 为空，需先恢复会话再判断）
    if (!authStore.isAuthenticated) {
      await authStore.restoreSession()
    }
    if (authStore.isAuthenticated) {
      next('/')
      return
    }
  }

  if (to.meta.permissionId) {
    const hasPermission = authStore.hasPermission(to.meta.permissionId as string)
    if (!hasPermission) {
      // 防自跳死循环（0924）：Dashboard 自身带 DASHBOARD_VIEW，无权限时再 next(Dashboard) 会无限重定向卡死页面
      if (to.name === 'Dashboard') {
        next()
        return
      }
      next({ name: 'Dashboard' })
      return
    }
  }

  next()
})

router.afterEach((to) => {
  const appStore = useAppStore()
  const crumbs: { title: string; path?: string }[] = []

  if (to.meta.title) {
    if (to.meta.group) {
      crumbs.push({ title: to.meta.group as string })
    }
    crumbs.push({ title: to.meta.title as string, path: to.fullPath })
  }

  appStore.setBreadcrumbs(crumbs)
})

export default router
